import { withTransaction } from '@elastic/apm-rum-react'
import { DakenService, LogService, MySquid, onConfigChange, Selector, User, UserHelper } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './notifications-form.page.scoped.scss'

const NotificationsFormPage = ({ impersonateUser = null }: any) => {
  const [loading, setLoading] = useState<boolean>(true)
  const logService = useMemo(() => new LogService(), [])
  const [config, setConfig] = useState<MySquid | null>(null)
  const dakenService = useMemo(() => new DakenService(), [])
  const [user, setUser] = useState<User | null>(null)
  const [loaderField, setLoaderField] = useState<boolean>(false)
  const userHelper = useMemo(() => new UserHelper(), [])
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const { t } = useTranslation('notificationsFormPage')
  logService.init('Notification Config Page')

  useEffect(() => {
    userHelper.getUser().then((user) => {
      setUser(user)
      setLoading(false)
    })
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })
  }, [userHelper])

  const updateInfo = useCallback(
    async (
      fieldName: 'allowSMS' | 'allowWhatsapp' | 'allowSuggestionEmail' | 'allowBrandLovers' | 'allowAffiliates' | 'allowResearch',
      checked: boolean
    ) => {
      if (user) {
        try {
          setLoaderField(true)
          const response = await dakenService.patchInflucencersNotificationsConfig(user?.squidId, fieldName)
          if (!response) {
            throw new Error('Failed to update communications')
          }
          const newUser: User = { ...user, profileadditionalinfo: { ...user?.profileadditionalinfo, [fieldName]: checked ? 1 : 0 } }
          setUser(newUser)
          userHelper.setUser(newUser)
          setLoaderField(false)
        } catch (error) {
          logService.error({
            error,
            message: 'Error updating user comunications info',
          })
          setLoaderField(false)
        }
      }
    },
    [user, dakenService, logService, userHelper]
  )

  let content = (
    <div className="loader-wrapper">
      <SqLoader size="big" />
    </div>
  )
  if (!loading) {
    content = (
      <div>
        <ul>
          <li>
            <h6>{t('message.title')}</h6>
            <p className="notifications-page-comment">{t('message.tip')}</p>
          </li>
          <li>
            <Selector
              leftLabel={
                <span dangerouslySetInnerHTML={{ __html: t('message.sms') }} style={{ fontSize: 'inherit', lineHeight: 'inherit' }} />
              }
              name="allow-sms-toggle"
              id="allow-sms-toggle"
              wrapperClass="justify-content-space-between"
              onChange={(checked: boolean) => updateInfo('allowSMS', checked)}
              checked={!!user?.profileadditionalinfo?.allowSMS}
              readOnly={loaderField || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
              errorSpan={false}
              toggle
            />
          </li>
          <li>
            <Selector
              leftLabel={
                <span dangerouslySetInnerHTML={{ __html: t('message.whatsapp') }} style={{ fontSize: 'inherit', lineHeight: 'inherit' }} />
              }
              name="allow-whatsapp-toggle"
              id="allow-whatsapp-toggle"
              wrapperClass="justify-content-space-between"
              onChange={(checked: boolean) => updateInfo('allowWhatsapp', checked)}
              checked={!!user?.profileadditionalinfo?.allowWhatsapp}
              readOnly={loaderField || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
              errorSpan={false}
              toggle
            />
          </li>
        </ul>
        <ul className="mt-5">
          <li>
            <h6>{t('email.title')}</h6>
          </li>
          <li>
            <Selector
              leftLabel={<span dangerouslySetInnerHTML={{ __html: t('email.opportunity') }} />}
              name="allow-opportunity-toggle"
              wrapperClass="justify-content-space-between"
              id="allow-opportunity-toggle"
              onChange={(checked: boolean) => updateInfo('allowSuggestionEmail', checked)}
              checked={!!user?.profileadditionalinfo?.allowSuggestionEmail}
              readOnly={loaderField || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
              errorSpan={false}
              toggle
            />
            <p className="notifications-page-comment">{t('email.opportunityTip')}</p>
          </li>
          <li>
            <Selector
              leftLabel={<span dangerouslySetInnerHTML={{ __html: t('email.bl') }} />}
              name="allow-bl-toggle"
              id="allow-bl-toggle"
              wrapperClass="justify-content-space-between"
              onChange={(checked: boolean) => updateInfo('allowBrandLovers', checked)}
              checked={!!user?.profileadditionalinfo?.allowBrandLovers}
              readOnly={loaderField || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
              errorSpan={false}
              toggle
            />
            <p className="notifications-page-comment">{t('email.blTip')}</p>
          </li>
          <li>
            <Selector
              leftLabel={<span dangerouslySetInnerHTML={{ __html: t('email.affiliates') }} />}
              name="allow-affiliates-toggle"
              id="allow-affiliates-toggle"
              wrapperClass="justify-content-space-between"
              onChange={(checked: boolean) => updateInfo('allowAffiliates', checked)}
              checked={!!user?.profileadditionalinfo?.allowAffiliates}
              readOnly={loaderField || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
              errorSpan={false}
              toggle
            />
            <p className="notifications-page-comment">{t('email.affiliatesTip')}</p>
          </li>
          <li>
            <Selector
              leftLabel={<span dangerouslySetInnerHTML={{ __html: t('email.research') }} />}
              name="allow-research-toggle"
              id="allow-research-toggle"
              wrapperClass="justify-content-space-between"
              onChange={(checked: boolean) => updateInfo('allowResearch', checked)}
              checked={!!user?.profileadditionalinfo?.allowResearch}
              readOnly={loaderField || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
              errorSpan={false}
              toggle
            />
            <p className="notifications-page-comment">{t('email.researchTip')}</p>
          </li>
          <li>
            <Selector
              leftLabel={<span dangerouslySetInnerHTML={{ __html: t('email.notifications') }} />}
              name="allow-notifications-toggle"
              id="allow-notifications-toggle"
              wrapperClass="justify-content-space-between"
              checked={true}
              disabled={true}
              errorSpan={false}
              toggle
            />
            <p className="notifications-page-comment">{t('email.notificationsTip')}</p>
          </li>
        </ul>
      </div>
    )
  }
  return <div className="notifications-config-page">{content}</div>
}

export default withTransaction('NotificationsFormPage', 'component')(NotificationsFormPage)
